import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidate|Program History|Other Achievements Form';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.CandidateAccessLoginEmcOtherAchievements,
	portal: 'SP',
};
const fieldLabelDescriptionStart = 'Label for form field: ';

const AGREEMENT_CONTENT = `<p>
			As an employee of Dell Technologies, I agree to abide by all published Dell Technologies rules and
			regulations, including those regarding non-disclosure of Dell Technologies private information. I also agree
			that if requested, I will supply proof of credentials I am entering. I give Dell Technologies permission to
			share my industry credentials with Dell Technologies Management.
		</p>
		<p>
			Note: This website tracks internal certifications so that your skill profile can be complete and accurate.
			Information provided on industry certifications achieved will be used by Dell Technologies management to
			assess overall field skill sets and qualifications. List only certifications received and current (i.e. do
			not include any certifications that have expired). Self reported certifications may be audited to verify
			accuracy and validity
		</p>`;

type LocNameStart = 'SPCandidateHistoryOtherAchievementForm';
export type SPCandidateHistoryOtherAchievementFormLocNames =
	| `${LocNameStart}LabelVendor`
	| `${LocNameStart}LabelVerificationUrl`
	| `${LocNameStart}ErrorVerificationUrl`
	| `${LocNameStart}LabelVerified`
	| `${LocNameStart}ErrorVendorRequired`
	| `${LocNameStart}LabelName`
	| `${LocNameStart}ErrorNameRequired`
	| `${LocNameStart}LabelExpirationDate`
	| `${LocNameStart}LabelDateTaken`
	| `${LocNameStart}LabelActiveDate`
	| `${LocNameStart}LabelExpireDate`
	| `${LocNameStart}ErrorActiveDate`
	| `${LocNameStart}ErrorDateTaken`
	| `${LocNameStart}LabelExternalId`
	| `${LocNameStart}ErrorExternalId`
	| `${LocNameStart}ContentExpireHint`
	| `${LocNameStart}ContentAgreement`
	| `${LocNameStart}ButtonCancel`
	| `${LocNameStart}ButtonAddCert`
	| `${LocNameStart}ButtonUpdateCert`
	| `${LocNameStart}LabelReimbursement`
	| `${LocNameStart}NotificationSuccess`
	| `${LocNameStart}NotificationFailure`
	| `${LocNameStart}ErrorActiveDateMax`
	| `${LocNameStart}ErrorDateComparison`;

export const spCandidateHistoryOtherAchievementForm: LocDefs<SPCandidateHistoryOtherAchievementFormLocNames> =
	{
		SPCandidateHistoryOtherAchievementFormLabelVendor: {
			defaultValue: 'Vendor/Sponsor',
			description: `${fieldLabelDescriptionStart}Vendor/Sponsor.`,
			displayPath: `${pathStart}|Vendor`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormLabelVerified: {
			defaultValue: 'Verified	',
			description: `${fieldLabelDescriptionStart}Verified.`,
			displayPath: `${pathStart}|Verified`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormErrorVendorRequired: {
			defaultValue: 'Vendor/Sponsor is required',
			description: `Error shown when Vendor/Sponsor is not selected.`,
			displayPath: `${pathStart}|Vendor/Sponsor required`,
			displayType: 'Error',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormLabelName: {
			defaultValue: 'Industry Certification Name',
			description: `${fieldLabelDescriptionStart}Name.`,
			displayPath: `${pathStart}|Name`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormErrorNameRequired: {
			defaultValue: 'Industry certification name is required',
			description: `Error shown when industry certification name is not selected.`,
			displayPath: `${pathStart}|Industry certification name required`,
			displayType: 'Error',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormLabelReimbursement: {
			defaultValue: 'Reimbursement Available',
			description: `${fieldLabelDescriptionStart}Reimbursement available.`,
			displayPath: `${pathStart}|Reimbursement Available`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormLabelExpirationDate: {
			defaultValue: 'Expiration Date',
			description: `${fieldLabelDescriptionStart}Expiration date.`,
			displayPath: `${pathStart}|Name`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormLabelDateTaken: {
			defaultValue: 'Date Taken',
			description: `${fieldLabelDescriptionStart}Date taken.`,
			displayPath: `${pathStart}|Date taken`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormLabelExternalId: {
			defaultValue: 'Certification ID',
			description: `${fieldLabelDescriptionStart}External Id.`,
			displayPath: `${pathStart}|External Id`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormErrorExternalId: {
			defaultValue: 'Certification ID is required',
			description: `Error shown when external id is not provided.`,
			displayPath: `${pathStart}|External Id Required`,
			displayType: 'Error',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormLabelActiveDate: {
			defaultValue: 'Date Achieved',
			description: `${fieldLabelDescriptionStart}Date achieved.`,
			displayPath: `${pathStart}|Achieved Date`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormErrorActiveDate: {
			defaultValue: 'Achievement date is required',
			description: `Error shown when industry certification name is not selected.`,
			displayPath: `${pathStart}|Industry certification name required`,
			displayType: 'Error',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormErrorDateTaken: {
			defaultValue: 'Date taken is required',
			description: `Error shown when taken date is not selected.`,
			displayPath: `${pathStart}|Date Taken Required`,
			displayType: 'Error',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormLabelExpireDate: {
			defaultValue: 'Expiration Date',
			description: `${fieldLabelDescriptionStart}Expiration Date.`,
			displayPath: `${pathStart}|Expiration Date`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormContentExpireHint: {
			defaultValue: 'Leave this blank if the certification does not expire',
			description: 'Hint for expiration date field.',
			displayPath: `${pathStart}|Expiration Date Hint`,
			displayType: 'Content',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormLabelVerificationUrl: {
			defaultValue: 'Verification Url',
			description: `${fieldLabelDescriptionStart}Verification Url.`,
			displayPath: `${pathStart}|Verification Url`,
			displayType: 'Label',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormErrorVerificationUrl: {
			defaultValue: 'Verification Url is required',
			description: `Error shown when verification url is not provided.`,
			displayPath: `${pathStart}|Verification Url Required`,
			displayType: 'Error',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormContentAgreement: {
			defaultValue: AGREEMENT_CONTENT,
			description: 'Agreement content by Dell.',
			displayPath: `${pathStart}|AgreementContent`,
			displayType: 'Content',
			shared: false,
			htmlAllowed: true,
			feature: HubFeature.CandidateAccessLoginEmcOtherAchievements,
			portal: 'SP',
		},
		SPCandidateHistoryOtherAchievementFormButtonCancel: {
			defaultValue: 'Cancel',
			description: `Button to cancel the form.`,
			displayPath: `${pathStart}|Cancel Button`,
			displayType: 'Button',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormButtonAddCert: {
			defaultValue: 'Add Certification',
			description: `Button to add certification.`,
			displayPath: `${pathStart}|Add Certification Button`,
			displayType: 'Button',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormButtonUpdateCert: {
			defaultValue: 'Update Certification',
			description: `Button to update certification.`,
			displayPath: `${pathStart}|Update Certification Button`,
			displayType: 'Button',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormNotificationSuccess: {
			defaultValue: 'Changes saved.',
			description: `Notification message shown when candidate other achievement changes are saved successfully.`,
			displayPath: `${pathStart}|Changes Saved`,
			displayType: 'Notification',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormNotificationFailure: {
			defaultValue: 'System error occurred while saving your changes.',
			description: `Notification message shown when candidate other achievement changes are not saved.`,
			displayPath: `${pathStart}|Changes Failed`,
			displayType: 'Notification',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormErrorActiveDateMax: {
			defaultValue: 'Achievement date cannot be in the future',
			description: `Error shown when achievement date is in the future.`,
			displayPath: `${pathStart}|Achievement Date Future`,
			displayType: 'Error',
			...sharedProperties,
		},
		SPCandidateHistoryOtherAchievementFormErrorDateComparison: {
			defaultValue: 'Expiration date must be after achievement date',
			description: `Error shown when achievement date is after expiration date.`,
			displayPath: `${pathStart}|Invalid Date Combination`,
			displayType: 'Error',
			...sharedProperties,
		},
	};

export type SPCandidateHistoryOtherAchievementFormLocType =
	LocMap<SPCandidateHistoryOtherAchievementFormLocNames>;
